import { Header } from '@/components/Header';
import { Hero } from '@/components/Hero';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { SEO, LocalBusinessSchema, SpeakableSchema, FaqSchema, BreadcrumbSchema } from '@/components/SEO';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Star, MapPin, Quote
} from 'lucide-react';
import deckpro1 from '@/assets/deckpro (1).jpeg';
import deckpro9 from '@/assets/deckpro (9).jpeg';
import deckpro24 from '@/assets/deckpro (24).jpeg';
import deckpro5 from '@/assets/deckpro (5).jpeg';
import deckpro7 from '@/assets/deckpro (7).jpeg';
import deckpro15 from '@/assets/deckpro (15).jpeg';

/* ─── Services ──────────────────────────────────────────── */
const ServicesSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const services = [
    { title: 'Custom Deck Design', description: 'Tailored designs built from the ground up to match your home.', image: deckpro15 },
    { title: 'Deck Replacement', description: 'Full replacement with premium composite, PVC, or hardwood.', image: deckpro7 },
    { title: 'Deck Repairs', description: 'Structural repairs, board replacement, and restoration.', image: deckpro9 },
    { title: 'Outdoor Living', description: 'Pergolas, railings, pavers, and full outdoor rooms.', image: deckpro1 },
  ];

  return (
    <section className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10 max-w-2xl mx-auto"
        >
          <p className="section-eyebrow">What We Do Best</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-3 text-navy">
            American-Built <span className="text-patriot">Decks</span>
          </h2>
          <p className="text-stone text-base leading-relaxed font-body">
            Every board hand-selected, every cut precise. Here's what we build.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto mb-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.08 }}
              className="group bg-white border-2 border-navy/10 hover:border-patriot transition-all duration-200 hover:-translate-y-1 hover:shadow-hard"
            >
              <div className="relative h-40 overflow-hidden">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-navy/20 group-hover:bg-navy/5 transition-colors" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-base font-bold mb-2 text-navy uppercase tracking-wide group-hover:text-patriot transition-colors">
                  {service.title}
                </h3>
                <p className="text-stone text-sm leading-relaxed font-body">
                  {service.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/services">
            <Button variant="cta" size="lg" className="rounded-none font-display uppercase tracking-[0.15em] text-sm border-2 border-patriot-dark shadow-hard hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ─── Why Choose Us ─────────────────────────────────────── */
const WhyChooseUs = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section className="section-compact bg-navy text-white relative overflow-hidden">
      <div className="container mx-auto px-4 relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-7xl mx-auto">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="overflow-hidden border-4 border-cream shadow-hard">
              <img src={deckpro5} alt="Professional deck construction by DeckPros NJ — American craftsmanship" className="w-full h-[380px] lg:h-[460px] object-cover" />
            </div>
            {/* Years badge */}
            <div className="absolute -bottom-5 -right-5 bg-patriot border-4 border-cream p-5 shadow-hard">
              <div className="font-display text-4xl font-bold text-white leading-none">25+</div>
              <div className="font-display text-[10px] uppercase tracking-[0.2em] text-white/90 mt-1">Years in NJ</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="order-1 lg:order-2"
          >
            <p className="section-eyebrow" style={{ color: 'hsl(var(--patriot))' }}>Why DeckPros NJ</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Real Americans. <span className="text-patriot">Real Craftsmanship.</span>
            </h2>
            <p className="text-white/70 text-base leading-relaxed mb-6 font-body">
              For over a quarter century, we've built decks the right way — in-house, no subcontractors, no shortcuts. Just honest work from a family who's been doing this since 1999.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-7">
              {[
                '25+ Years of Experience',
                'Licensed, Bonded & Insured',
                'Lifetime Workmanship Warranty',
                'Free On-Site Consultation',
                '1,000+ Decks Built in NJ',
                'No Subcontractors — Ever',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-patriot flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-sm font-body font-medium">{item}</span>
                </div>
              ))}
            </div>

            <Link to="/about">
              <Button variant="cta" size="lg" className="rounded-none font-display uppercase tracking-[0.15em] text-sm border-2 border-cream shadow-hard hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                Our Story
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─── Featured Projects ─────────────────────────────────── */
const FeaturedProjectsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const projects = [
    { image: deckpro24, title: 'Premium Composite Deck', location: 'Morris County', tag: 'Composite' },
    { image: deckpro9, title: 'Multi-Level w/ Under-Deck Living', location: 'Sussex County', tag: 'Multi-Level' },
    { image: deckpro1, title: 'Pressure-Treated Classic', location: 'Warren County', tag: 'Wood' },
    { image: deckpro5, title: 'Custom Backyard w/ Staircase', location: 'Somerset County', tag: 'Custom' },
  ];

  return (
    <section className="section-compact bg-cream-dark">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 max-w-7xl mx-auto"
        >
          <div>
            <p className="section-eyebrow">Our Work</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy">
              Recent <span className="text-patriot">Projects</span>
            </h2>
          </div>
          <Link to="/gallery">
            <Button variant="outline" size="default" className="rounded-none font-display uppercase tracking-[0.15em] text-xs border-2 border-navy text-navy hover:bg-navy hover:text-white">
              View Full Gallery
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 max-w-7xl mx-auto">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.06 }}
              className="group"
            >
              <Link to="/projects" className="block">
                <div className="relative overflow-hidden border-2 border-navy/10 hover:border-patriot transition-all duration-200 aspect-[4/5]">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block bg-patriot text-white text-[9px] font-display font-bold uppercase tracking-[0.18em] px-2.5 py-1">
                      {project.tag}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-display font-bold text-white text-sm md:text-base leading-snug uppercase tracking-wide group-hover:text-patriot transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-cream/70 text-[11px] mt-1 font-body flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {project.location}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Testimonials ──────────────────────────────────────── */
const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const testimonials = [
    { name: 'John Smith', location: 'Morris County, NJ', text: 'DeckPros transformed our backyard into the perfect outdoor oasis. Professional, punctual, and the final product exceeded our expectations!' },
    { name: 'Sarah Johnson', location: 'Sussex County, NJ', text: 'Best decision we made for our home. The design consultation was incredibly helpful and the installation was flawless.' },
    { name: 'Mike Rodriguez', location: 'Somerset County, NJ', text: 'Transparent pricing, outstanding results. Our new deck is the talk of the neighborhood. Thank you DeckPros!' },
  ];

  return (
    <section className="section-compact bg-cream">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-10"
        >
          <p className="section-eyebrow">Happy Homeowners</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy">
            What New Jersey <span className="text-patriot">Says</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {testimonials.map((t, index) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="bg-white border-2 border-navy/10 p-6 relative hover:border-patriot transition-colors group"
            >
              <Quote className="absolute -top-3 left-5 w-8 h-8 text-patriot bg-cream p-1" />
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber text-amber" />
                ))}
              </div>
              <p className="text-stone text-sm leading-relaxed mb-5 font-body">
                "{t.text}"
              </p>
              <div className="flex items-center gap-3 pt-4 border-t-2 border-navy/10">
                <div className="w-10 h-10 bg-navy flex items-center justify-center text-patriot font-display font-bold flex-shrink-0 border-2 border-patriot">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-display font-bold text-navy text-sm uppercase tracking-wide">{t.name}</div>
                  <div className="text-[11px] text-stone flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3 text-patriot" />
                    {t.location}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Service Areas ─────────────────────────────────────── */
const ServiceAreasSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  const counties = [
    { name: 'Morris', path: '/areas/morris-county' },
    { name: 'Sussex', path: '/areas/sussex-county' },
    { name: 'Warren', path: '/areas/warren-county' },
    { name: 'Hunterdon', path: '/areas/hunterdon-county' },
    { name: 'Somerset', path: '/areas/somerset-county' },
  ];

  return (
    <section className="section-snug bg-cream-dark">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mb-7"
        >
          <p className="section-eyebrow justify-center">Coverage Area</p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy">
            Serving All of <span className="text-patriot">New Jersey</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap justify-center gap-2"
        >
          {counties.map((county) => (
            <Link
              key={county.name}
              to={county.path}
              className="flex items-center gap-2 bg-white border-2 border-navy text-navy hover:bg-patriot hover:text-white hover:border-patriot px-4 py-2.5 font-display uppercase text-xs font-bold tracking-[0.15em] transition-all"
            >
              <MapPin className="w-3 h-3" />
              {county.name} County
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ─── Page ──────────────────────────────────────────────── */
const HomePage = () => {
  /* Hidden FAQ data — schema only for AEO (no visual FAQ per user request) */
  const aeoFaqs = [
    { question: 'How much does a new deck cost in New Jersey?', answer: 'Deck costs in NJ range from $15–25/sq ft for pressure-treated wood, $30–45/sq ft for composite decking, and $50–80+/sq ft for premium materials. DeckPros NJ provides free detailed estimates with transparent pricing.' },
    { question: 'Do I need a permit to build a deck in NJ?', answer: 'Yes, most New Jersey municipalities require a building permit for decks attached to the home or over 30 inches high. DeckPros NJ handles all permitting and inspections for you.' },
    { question: 'How long does it take to build a deck?', answer: 'Most residential decks take 3–7 days to build. Simple replacements can be completed in 2–3 days; complex multi-level designs can take 1–2 weeks.' },
    { question: 'What areas in New Jersey does DeckPros NJ serve?', answer: 'DeckPros NJ serves Morris, Sussex, Warren, Hunterdon, and Somerset Counties, plus all of northern and central New Jersey.' },
    { question: 'Is DeckPros NJ licensed and insured?', answer: 'Yes — DeckPros NJ is fully licensed, bonded, and insured in the state of New Jersey, with 25+ years in business and an A+ BBB rating.' },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Home"
        description="Family-owned NJ deck builders with 25+ years of experience. Custom decks, composite decking, repairs & outdoor living. Licensed, bonded, insured. Call (201) 657-0855 for a free estimate."
        canonical="/"
        keywords="deck builder NJ, New Jersey deck contractor, custom deck design, composite decking, deck repair, Morris County deck builder"
      />
      <LocalBusinessSchema />
      <SpeakableSchema />
      <FaqSchema faqs={aeoFaqs} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }]} />
      <Header />
      <main>
        <Hero />
        <ServicesSection />
        <WhyChooseUs />
        <FeaturedProjectsSection />
        <TestimonialsSection />
        <ServiceAreasSection />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
